import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CalEvent, Calendar, Me, Scope, ViewName } from "../api/types";
import { addDays, addMonths, diffDays, nowIn, rangeDays, startOfMonth, startOfWeek, weekday, type DateStr } from "../lib/date";
import type { Draft } from "../lib/draft";

export type Page = "home" | "calendar" | "tasks" | "resolutions" | "health" | "finance" | "cycle" | "settings" | "search" | "admin";

export interface Anchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function anchorOf(el: Element | null, fallback?: { x: number; y: number }): Anchor {
  if (el) {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }
  return { x: fallback?.x ?? window.innerWidth / 2, y: fallback?.y ?? 120, width: 0, height: 0 };
}

export type Overlay =
  | { kind: "quick"; draft: Draft; anchor: Anchor }
  | { kind: "details"; event: CalEvent; anchor: Anchor }
  | { kind: "editor"; draft: Draft; event?: CalEvent }
  | { kind: "scope"; mode: "edit" | "delete"; onChoose: (scope: Scope) => void; onCancel?: () => void }
  | { kind: "shortcuts" }
  | null;

export interface Toast {
  id: number;
  text: string;
  action?: { label: string; run: () => void };
}

interface UiValue {
  me: Me;
  calendars: Calendar[];
  view: ViewName;
  date: DateStr;
  today: DateStr;
  page: Page;
  searchQuery: string;
  overlay: Overlay;
  toast: Toast | null;
  /** Days shown by the current view (time grid / month grid). */
  visibleDays: DateStr[];
  /** Agenda category filter: category keys to show; empty = everything. */
  categoryFilter: string[];
  setCategoryFilter: (keys: string[]) => void;
  setView: (v: ViewName) => void;
  setDate: (d: DateStr) => void;
  goToday: () => void;
  step: (dir: 1 | -1) => void;
  setPage: (p: Page) => void;
  search: (q: string) => void;
  setOverlay: (o: Overlay) => void;
  showToast: (text: string, action?: Toast["action"]) => void;
  /** Resolves with the scope the user picked (null if cancelled) — only asks for recurring events. */
  askScope: (event: CalEvent, mode: "edit" | "delete") => Promise<Scope | null>;
}

const UiContext = createContext<UiValue | null>(null);

export function useUi(): UiValue {
  const v = useContext(UiContext);
  if (!v) throw new Error("useUi outside UiProvider");
  return v;
}

const CATEGORY_FILTER_KEY = "agenda.categoryFilter";

/** The filter is a per-browser convenience: storage may be missing or blocked. */
function readCategoryFilter(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CATEGORY_FILTER_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((k) => typeof k === "string") : [];
  } catch {
    return [];
  }
}

const VIEWS: ViewName[] = ["day", "week", "month", "year", "schedule", "4days"];

function readUrl(): { view?: ViewName; date?: DateStr; page: Page; q: string } {
  const params = new URLSearchParams(window.location.search);
  const view = params.get("view") as ViewName | null;
  const date = params.get("date");
  const path = window.location.pathname;
  // "/?view=…&date=…" is the agenda's old address (bookmarks, notifications sent before the home page existed).
  const page: Page = path.startsWith("/admin")
    ? "admin"
    : path.startsWith("/configuracoes")
    ? "settings"
    : path.startsWith("/buscar")
      ? "search"
      : path.startsWith("/tarefas")
        ? "tasks"
        : path.startsWith("/resolucoes")
          ? "resolutions"
          : path.startsWith("/saude")
            ? "health"
            : path.startsWith("/financas")
              ? "finance"
              : path.startsWith("/ciclo")
                ? "cycle"
                : path.startsWith("/agenda") || view || date
            ? "calendar"
            : "home";
  return {
    view: view && VIEWS.includes(view) ? view : undefined,
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined,
    page,
    q: params.get("q") ?? ""
  };
}

/** Days displayed by a view anchored at `date`. */
export function daysForView(view: ViewName, date: DateStr, weekStart: number, showWeekends: boolean): DateStr[] {
  switch (view) {
    case "day":
      return [date];
    case "4days":
      return rangeDays(date, 4);
    case "week": {
      const days = rangeDays(startOfWeek(date, weekStart), 7);
      return showWeekends ? days : days.filter((d) => weekday(d) !== 0 && weekday(d) !== 6);
    }
    case "month": {
      // 5 or 6 week rows, as many as the month needs.
      const first = startOfWeek(startOfMonth(date), weekStart);
      const lastOfMonth = addDays(startOfMonth(addMonths(startOfMonth(date), 1)), -1);
      const weeks = Math.ceil((diffDays(first, lastOfMonth) + 1) / 7);
      return rangeDays(first, weeks * 7);
    }
    default:
      return [date];
  }
}

export function UiProvider({ me, calendars, children }: { me: Me; calendars: Calendar[]; children: ReactNode }) {
  const initial = useMemo(readUrl, []);
  const [today, setToday] = useState(() => nowIn(me.timezone).date);
  const narrow = typeof window !== "undefined" && window.innerWidth < 720;
  const [view, setViewState] = useState<ViewName>(initial.view ?? (narrow && me.settings.defaultView === "week" ? "4days" : me.settings.defaultView));
  const [date, setDateState] = useState<DateStr>(initial.date ?? today);
  const [page, setPageState] = useState<Page>(initial.page);
  const [searchQuery, setSearchQuery] = useState(initial.q);
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [categoryFilter, setCategoryFilterState] = useState<string[]>(readCategoryFilter);
  const setCategoryFilter = useCallback((next: string[]) => {
    setCategoryFilterState(next);
    try {
      localStorage.setItem(CATEGORY_FILTER_KEY, JSON.stringify(next));
    } catch {
      // Not persisted; the filter still applies for this session.
    }
  }, []);

  // Keep "today" right across midnight.
  useEffect(() => {
    const t = setInterval(() => setToday(nowIn(me.timezone).date), 60_000);
    return () => clearInterval(t);
  }, [me.timezone]);

  // URL <-> state (shareable links, back/forward).
  useEffect(() => {
    const params = new URLSearchParams();
    let path = "/";
    if (page === "settings") path = "/configuracoes";
    else if (page === "admin") path = "/admin";
    else if (page === "tasks") path = "/tarefas";
    else if (page === "resolutions") path = "/resolucoes";
    else if (page === "health") path = "/saude";
    else if (page === "finance") path = "/financas";
    else if (page === "cycle") path = "/ciclo";
    else if (page === "search") {
      path = "/buscar";
      params.set("q", searchQuery);
    } else if (page === "calendar") {
      path = "/agenda";
      params.set("view", view);
      params.set("date", date);
    }
    const url = `${path}${params.toString() ? `?${params}` : ""}`;
    if (url !== window.location.pathname + window.location.search) window.history.pushState(null, "", url);
  }, [view, date, page, searchQuery]);

  useEffect(() => {
    const onPop = () => {
      const s = readUrl();
      if (s.view) setViewState(s.view);
      if (s.date) setDateState(s.date);
      setPageState(s.page);
      setSearchQuery(s.q);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const toastTimer = useMemo(() => ({ id: 0 as unknown as ReturnType<typeof setTimeout> }), []);
  const showToast = useCallback(
    (text: string, action?: Toast["action"]) => {
      clearTimeout(toastTimer.id);
      const t = { id: Date.now(), text, action };
      setToast(t);
      toastTimer.id = setTimeout(() => setToast((cur) => (cur?.id === t.id ? null : cur)), action ? 8000 : 4000);
    },
    [toastTimer]
  );

  const askScope = useCallback((event: CalEvent, mode: "edit" | "delete") => {
    if (!event.isRecurring) return Promise.resolve<Scope | null>("all");
    return new Promise<Scope | null>((resolve) => {
      setOverlay({
        kind: "scope",
        mode,
        onChoose: (scope) => {
          setOverlay(null);
          resolve(scope);
        },
        onCancel: () => {
          setOverlay(null);
          resolve(null);
        }
      });
    });
  }, []);

  const step = useCallback(
    (dir: 1 | -1) => {
      setDateState((d) => {
        switch (view) {
          case "day":
            return addDays(d, dir);
          case "4days":
            return addDays(d, 4 * dir);
          case "week":
          case "schedule":
            return addDays(d, 7 * dir);
          case "month":
            return addMonths(d, dir);
          case "year":
            return addMonths(d, 12 * dir);
        }
      });
    },
    [view]
  );

  const value: UiValue = {
    me,
    calendars,
    view,
    date,
    today,
    page,
    searchQuery,
    overlay,
    toast,
    visibleDays: daysForView(view, date, me.settings.weekStart, me.settings.showWeekends),
    categoryFilter,
    setCategoryFilter,
    setView: (v) => {
      setViewState(v);
      setPageState("calendar");
    },
    setDate: (d) => {
      setDateState(d);
      setPageState("calendar");
    },
    goToday: () => {
      setDateState(today);
      setPageState("calendar");
    },
    step,
    setPage: setPageState,
    search: (q) => {
      setSearchQuery(q);
      setPageState(q.trim() ? "search" : "calendar");
    },
    setOverlay,
    showToast,
    askScope
  };

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}
